import React, { createContext, useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import useOrgId from 'hooks/useOrgId.js';
import useUpdateUserMutation from './hooks/useUpdateUserMutation.js';
import useUpdateAvatarMutation from './hooks/useUpdateAvatarMutation.js';
import useDeleteAvatarMutation from './hooks/useDeleteAvatarMutation.js';
import useUserDataQuery from './hooks/useUserDataQuery.js';

const orgUserInitialValues = {
  displayName: '',
  avatar: null,
  fullName: '',
  jobTitle: '',
  email: '',
  calendarId: '',
  calendars: [],
  roles: [],
  locale: '',
  userId: '',
  optOutPlatformEmails: false,
};

export const OrgUserContext = createContext({ userData: orgUserInitialValues, loading: false });

export const OrgUserContextProvider = ({ children }) => {
  const orgId = useOrgId();
  const uid = useSelector((state) => state.sessionState.authUser?.uid);
  const queryClient = useQueryClient();
  const { data: userData = orgUserInitialValues, isLoading } = useUserDataQuery();
  const deleteAvatarMutation = useDeleteAvatarMutation();
  const updateAvatarMutation = useUpdateAvatarMutation();
  const updateUserMutation = useUpdateUserMutation();

  const refreshUserData = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: ['o', orgId, 'user', uid],
    });
  }, [orgId, queryClient, uid]);

  const update = useCallback(
    async ({ fullName, displayName, avatar, jobTitle, calendarId, locale, optOutPlatformEmails }, { catchCallback, finallyCallback } = {}) => {
      if (avatar !== undefined) {
        if (avatar === null) {
          deleteAvatarMutation.mutate();
        } else {
          updateAvatarMutation.mutate(avatar);
        }
      }

      const patch = {};
      if (displayName !== undefined) patch.displayName = displayName;
      if (fullName !== undefined) patch.fullName = fullName;
      if (jobTitle !== undefined) patch.jobTitle = jobTitle;
      if (calendarId !== undefined) patch.calendarId = calendarId;
      if (locale !== undefined) patch.locale = locale;
      if (optOutPlatformEmails !== undefined) patch.optOutPlatformEmails = optOutPlatformEmails;
      const didUpdate = Object.keys(patch).some((key) => patch[key] !== userData[key]);
      if (didUpdate) {
        updateUserMutation.mutate(patch, { onError: catchCallback, onSettled: finallyCallback });
      }
    },
    [userData, deleteAvatarMutation, updateAvatarMutation, updateUserMutation],
  );

  const context = {
    userData,
    loading: isLoading,
    updateUserData: update,
    refreshUserData,
    isSignedIn: !!uid,
    isUpdating: updateUserMutation.isLoading || updateAvatarMutation.isLoading || deleteAvatarMutation.isLoading,
    supportedFields: ['displayName', 'fullName', 'jobTitle', 'calendarId'],
    avatarUploadEnabled: true,
    userId: uid,
  };

  return <OrgUserContext.Provider value={context}>{children}</OrgUserContext.Provider>;
};

export const useOrgUser = () => useContext(OrgUserContext);
